import { sql } from "drizzle-orm";

/**
 * Authenticated viewer shape attached to `ctx.auth`.
 * `id` / `email` / `name` are normalized; every other field is optional and comes from better-auth
 * `session.user`, JWT payload, or DB row via {@link copySessionUserFieldsForRuntimeIdentity}.
 */
export type RuntimeAuthUser = {
  id: string;
  email: string;
  name?: string;
  [key: string]: unknown;
};

/** Never surface these on `ctx.auth` / JWT identity payloads (secrets + standard JWT claims). */
const OMIT_FROM_RUNTIME_AUTH_USER = new Set<string>([
  "password",
  "hashedPassword",
  "salt",
  "iat",
  "exp",
  "iss",
  "nbf",
  "aud",
  "jti",
  "sub",
]);

function isJsonScalar(value: unknown): value is string | number | boolean | null {
  return value === null || typeof value === "string" || typeof value === "number" || typeof value === "boolean";
}

/**
 * Shallow copy of plugin / profile fields from a better-auth user object or verified JWT payload.
 * Drops secrets, JWT reserved claims, nested objects, and non-JSON-safe values so `ctx.auth` stays a flat JSON-like bag.
 */
export function copySessionUserFieldsForRuntimeIdentity(user: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(user)) {
    if (OMIT_FROM_RUNTIME_AUTH_USER.has(key)) continue;
    const v = user[key];
    if (v === undefined || typeof v === "function" || typeof v === "symbol") continue;
    if (v instanceof Date) {
      out[key] = v.toISOString();
      continue;
    }
    if (isJsonScalar(v)) {
      out[key] = v;
      continue;
    }
    if (Array.isArray(v) && v.every(isJsonScalar)) {
      out[key] = v;
    }
  }
  return out;
}

const roleColumnSupport = new WeakMap<object, boolean>();

function getCacheKey(db: unknown): object | null {
  return db && (typeof db === "object" || typeof db === "function") ? (db as object) : null;
}

function isMissingRoleColumnError(error: unknown): boolean {
  let current: unknown = error;
  const messages: string[] = [];
  const seen = new Set<unknown>();
  for (let depth = 0; current && depth < 8; depth++) {
    if (typeof current === "object") {
      if (seen.has(current)) break;
      seen.add(current);
    }
    const code = (current as { code?: unknown })?.code;
    if (code === "42703") return true;

    if (current instanceof Error) messages.push(current.message);
    else if (typeof current === "string") messages.push(current);
    else if (typeof (current as { message?: unknown })?.message === "string") {
      messages.push((current as { message: string }).message);
    }

    current = (current as { cause?: unknown })?.cause;
  }

  const message = messages.join("\n");
  return /column\s+"?role"?\s+does not exist/i.test(message);
}

async function queryRuntimeAuthUser(
  db: unknown,
  userId: string,
  includeRole: boolean,
): Promise<RuntimeAuthUser | null> {
  const userRows = includeRole
    ? await (db as any).execute(
        sql`SELECT "id", "email", "name", "role" FROM "user" WHERE "id" = ${userId} LIMIT 1`,
      )
    : await (db as any).execute(sql`SELECT "id", "email", "name" FROM "user" WHERE "id" = ${userId} LIMIT 1`);
  const userRow = (userRows?.rows?.[0] ?? userRows?.[0]) as
    | { id?: string; email?: string; name?: string; role?: string }
    | undefined;
  if (!userRow?.id || !userRow?.email) return null;
  return {
    ...copySessionUserFieldsForRuntimeIdentity(userRow as Record<string, unknown>),
    id: String(userRow.id),
    email: String(userRow.email),
    name: typeof userRow.name === "string" ? userRow.name : undefined,
  } as RuntimeAuthUser;
}

export async function lookupRuntimeAuthUser(db: unknown, userId: string): Promise<RuntimeAuthUser | null> {
  const cacheKey = getCacheKey(db);
  const shouldTryExtendedColumns = cacheKey ? roleColumnSupport.get(cacheKey) !== false : true;

  if (shouldTryExtendedColumns) {
    try {
      const user = await queryRuntimeAuthUser(db, userId, true);
      if (cacheKey) roleColumnSupport.set(cacheKey, true);
      return user;
    } catch (error) {
      if (!isMissingRoleColumnError(error)) throw error;
      if (cacheKey) roleColumnSupport.set(cacheKey, false);
    }
  }

  return queryRuntimeAuthUser(db, userId, false);
}
